'use client';

import React, { useState, useMemo, useCallback, useEffect, ChangeEvent } from 'react';
import { CustomCheckbox } from '../components/forms/CustomCheckbox';

interface Kpi {
  hires?: number;
  cph?: number;
  tth?: number;
  showup?: number;
  attendance?: number;
  csat?: number;
  geo?: number;
}

interface Review {
  id: string;
  type: 'text' | 'video';
  company: string;
  sector: string;
  rating: number;
  year: number;
  title: string;
  author: string;
  avatar: string;
  cover: string;
  video?: string;
  kpi?: Kpi;
  excerpt: string;
  html?: string;
  date: string;
}

const REVIEWS: Review[] = [
  {
    id: 'r1', type: 'text', company: 'MarketPlace X', sector: 'IT-сфера и Интернет', rating: 5, year: 2025,
    title: '180 закрытий/кв. и −19% CPH', author: 'Директор по персоналу', avatar: 'https://placehold.co/96x96',
    cover: 'https://placehold.co/800x480?text=IT+Case',
    kpi: { hires: 180, cph: -19, showup: 96 },
    excerpt: 'Масштабировали RPO, автоматизировали коммуникации и отчётность в BI.',
    html: '<p>INTERIM выстроил воронку, подключил 7 каналов и BI-дашборд. <b>CPH снизился на 19%</b>, явка достигла <b>96%</b>.</p>',
    date: '2025-08-10'
  },
  {
    id: 'r2', type: 'video', company: 'Retail&Go', sector: 'Сфера обслуживания', rating: 5, year: 2025,
    title: 'Сезонный пик без потерь качества', author: 'HRBP', avatar: 'https://placehold.co/96x96',
    cover: 'https://placehold.co/800x480?text=Service+Video', video: 'https://www.youtube.com/watch?v=ysz5S6PUM-U',
    kpi: { hires: 120, tth: -25 },
    excerpt: 'За 3 недели вывели команду и закрыли сезон.',
    html: '<p>Сжатые сроки, единые SLA, быстрый онбординг рекрутеров.</p>',
    date: '2025-07-22'
  },
  {
    id: 'r3', type: 'text', company: 'LogiPro', sector: 'Транспорт и логистика', rating: 4, year: 2025,
    title: 'Укомплектовали 3 смены на складе', author: 'Руководитель склада', avatar: 'https://placehold.co/96x96',
    cover: 'https://placehold.co/800x480?text=Logistics',
    kpi: { attendance: 92 },
    excerpt: 'Настроили слоты интервью и напоминания — no-show снизился.',
    html: '<p>Через SMS/Email/мессенджеры повысили явку до <b>92%</b>.</p>',
    date: '2025-11-05'
  },
  {
    id: 'r4', type: 'text', company: 'Supportly', sector: 'IT-сфера и Интернет', rating: 5, year: 2025,
    title: 'L1/L2 аутстафф: быстро и с QA', author: 'Head of Support', avatar: 'https://placehold.co/96x96',
    cover: 'https://placehold.co/800x480?text=IT+Support',
    kpi: { csat: 12 },
    excerpt: 'Собрали линию L1/L2, запустили обучение и контроль качества.',
    date: '2025-06-18'
  },
  {
    id: 'r5', type: 'video', company: 'Alma E-shop', sector: 'Торговля и маркетинг', rating: 4, year: 2025,
    title: 'Сложные регионы закрылись вовремя', author: 'HRD', avatar: 'https://placehold.co/96x96',
    cover: 'https://placehold.co/800x480?text=Video+Case', video: 'https://www.youtube.com/watch?v=ysz5S6PUM-U',
    kpi: { geo: 6 },
    excerpt: 'Подключили региональные источники и партнёров MSP.',
    html: '<p>Работали мультивендорно: единые KPI, прозрачная отчётность.</p>',
    date: '2025-09-14'
  },
];

const SECTORS = [
  'IT-сфера и Интернет',
  'Агрономия',
  'Администрация',
  'Культура и искусство',
  'Педагогика',
  'Строительство и ремонт',
  'Сфера обслуживания',
  'Сфера промышленности',
  'Торговля и маркетинг',
  'Транспорт и логистика',
  'Уход и здоровье'
];

const MIN_YEAR = 2025;
const MAX_YEAR = 2026;
const REVIEWS_PER_PAGE = 6;

const fmtDate = (s: string) => new Date(s).toLocaleDateString('ru-RU', { year: 'numeric', month: 'long', day: 'numeric' });
const fmtDateShort = (s: string) => new Date(s).toLocaleDateString('ru-RU');

const Stars: React.FC<{ n: number }> = ({ n }) => (
  <div className="stars">
    {Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < n ? 'star-on' : 'star-off'}>★</span>
    ))}
  </div>
);

const fmtImpact = (kpi?: Kpi): string => {
  if (!kpi) return '';
  const parts: string[] = [];
  if (kpi.hires) parts.push(`Закрыто: ${kpi.hires}`);
  if (kpi.cph !== undefined) parts.push(`CPH: ${kpi.cph > 0 ? '+' : ''}${kpi.cph}%`);
  if (kpi.tth !== undefined) parts.push(`Time-to-Hire: ${kpi.tth > 0 ? '+' : ''}${kpi.tth}%`);
  if (kpi.showup) parts.push(`Явка: ${kpi.showup}%`);
  if (kpi.attendance) parts.push(`Явка: ${kpi.attendance}%`);
  if (kpi.csat) parts.push(`CSAT: ${kpi.csat > 0 ? '+' : ''}${kpi.csat} п.п.`);
  if (kpi.geo) parts.push(`Регионов: ${kpi.geo}`);
  return parts.join(' • ');
};

const isYT = (u: string) => /youtube\.com|youtu\.be/.test(u);
const ytEmbed = (u: string): string => {
  try {
    if (!u) return '';
    const url = new URL(u);
    const id = url.hostname.includes('youtube.com')
      ? url.searchParams.get('v')
      : url.pathname.split('/').pop();
    return `https://www.youtube.com/embed/${id}?rel=0`;
  } catch {
    return '';
  }
};

const ReviewCard: React.FC<{ review: Review, openModal: (id: string) => void }> = ({ review, openModal }) => (
  <article className="card rounded-2xl overflow-hidden flex flex-col" itemScope itemType="https://schema.org/Review">
    <img src={review.cover} alt="" className="w-full h-44 object-cover" onError={(e) => (e.currentTarget.src = 'https://placehold.co/800x480?text=Case')} />
    <div className="p-5 flex-1 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xl font-semibold" itemProp="name">{review.title}</h3>
          <div className="text-sm text-gray-300" itemProp="author">{review.author} • <span itemProp="itemReviewed">{review.company}</span></div>
        </div>
        <div className="text-right">
          <Stars n={review.rating} />
          <meta itemProp="reviewRating" content={review.rating.toString()} />
        </div>
      </div>
      <div className="mt-2 text-xs flex flex-wrap gap-2">
        <span className="tag">{review.sector}</span>
        <span className="tag">Год: {review.year}</span>
        <span className="tag">{fmtDateShort(review.date)}</span>
      </div>
      <p className="text-sm text-gray-200 mt-3 line-clamp-3" itemProp="reviewBody">{review.excerpt || ''}</p>
      <div className="mt-3 text-xs text-gray-200">{fmtImpact(review.kpi)}</div>
      <div className="mt-auto pt-4 border-t border-white/10 flex items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          <span className="tag">{review.type === 'video' ? 'Видео-отзыв' : 'Текстовый отзыв'}</span>
        </div>
        <button className="btn btn-accent text-sm" onClick={() => openModal(review.id)}>Читать полностью</button>
      </div>
    </div>
  </article>
);

const ReviewModal: React.FC<{ review: Review | null, onClose: () => void }> = ({ review, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (review) {
      const timeout = setTimeout(() => setIsVisible(true), 10);
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') onClose();
      };
      document.addEventListener('keydown', handleEscape);
      return () => {
        clearTimeout(timeout);
        document.removeEventListener('keydown', handleEscape);
      };
    } else {
      setIsVisible(false);
    }
  }, [review, onClose]);

  if (!review) return null;

  const overlayClasses = isVisible ? 'opacity-100' : 'opacity-0';
  const contentClasses = isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95';
  const videoUrl = review.video ?? '';
  const media = review.type === 'video'
    ? (isYT(videoUrl)
      ? <div className="aspect-video w-full"><iframe src={ytEmbed(videoUrl)} allowFullScreen loading="lazy" title={review.title} className="w-full h-full rounded-xl"></iframe></div>
      : <div className="aspect-video w-full"><video src={videoUrl} controls playsInline className="w-full h-full rounded-xl"></video></div>)
    : null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${overlayClasses} bg-black/70`}
      aria-modal="true"
      role="dialog"
      aria-label="Отзыв"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`card rounded-2xl p-6 max-w-4xl max-h-[90vh] overflow-y-auto w-full relative transform transition-all duration-300 ${contentClasses}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute right-4 top-4 text-gray-400 hover:text-white transition-colors p-2 rounded-full z-10"
          aria-label="Закрыть"
          onClick={onClose}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <article className="mt-4">
          <div className="grid gap-6 md:grid-cols-2">
            <img src={review.cover} alt="" className="rounded-xl w-full h-56 object-cover" />
            <div className="flex flex-col justify-center">
              <h3 className="text-3xl font-bold">{review.title}</h3>
              <div className="mt-2 text-md text-gray-300">{review.author} • {review.company}</div>
              <div className="mt-2"><Stars n={review.rating} /></div>
              <div className="mt-3 text-xs flex flex-wrap gap-2">
                <span className="tag">{review.sector}</span>
                <span className="tag">Год: {review.year}</span>
                <span className="tag">{review.date && fmtDate(review.date)}</span>
              </div>
            </div>
          </div>
          {media && <div className="mt-6">{media}</div>}
          <div className="prose prose-invert max-w-none mt-6 text-base"
            dangerouslySetInnerHTML={{ __html: review.html ?? ('<p>' + (review.excerpt || '') + '</p>') }}
          />
          <div className="mt-6 text-sm font-semibold text-gray-400 border-t border-white/10 pt-4">
            KPI: {fmtImpact(review.kpi)}
          </div>
        </article>
      </div>
    </div>
  );
};

const STRAPI_API_URL = process.env.NEXT_PUBLIC_STRAPI_API_URL!;
const REVIEW_SUBMISSION_ENDPOINT = `${STRAPI_API_URL}/api/reviews-submissions`;

const ReviewSubmissionForm: React.FC = () => {
  const [company, setCompany] = useState('');
  const [author, setAuthor] = useState('');
  const [rating, setRating] = useState<string>('');
  const [reviewText, setReviewText] = useState('');
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const resetForm = useCallback(() => {
    setCompany('');
    setAuthor('');
    setRating('');
    setReviewText('');
    setIsConsentChecked(false);
    setStatus('success');
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isConsentChecked) {
      setStatus('error');
      setMessage('Пожалуйста, согласитесь с обработкой персональных данных.');
      return;
    }

    if (isSubmitting || status === 'success') return;

    setIsSubmitting(true);
    setStatus('idle');
    setMessage('');

    const submissionData = {
      formName: "Отзывы",
      company: company.trim(),
      author: author.trim(),
      rating: parseInt(rating, 10),
      review_text: reviewText.trim(),
      source: 'Reviews Page Submission',
    };

    try {
      const response = await fetch(REVIEW_SUBMISSION_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: submissionData }),
      });

      if (response.ok) {
        setMessage('Спасибо! Ваш отзыв успешно отправлен на модерацию.');
        resetForm();
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || response.statusText || 'Неизвестная ошибка сервера.';
        setStatus('error');
        setMessage(`Ошибка отправки в Strapi: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Network or other error:', error);
      setStatus('error');
      setMessage('Произошла ошибка сети. Проверьте подключение или попробуйте позже.');
    } finally {
      setIsSubmitting(false);
    }
  }, [company, author, rating, reviewText, isSubmitting, status, isConsentChecked, resetForm]);

  const handleConsentChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsConsentChecked(e.target.checked);
    if (e.target.checked && status === 'error' && message.includes('согласитесь с обработкой')) {
      setStatus('idle');
      setMessage('');
    }
  };

  const buttonText = useMemo(() => {
    if (isSubmitting) return 'Отправка...';
    if (status === 'success') return 'Успешно отправлено';
    return 'Отправить';
  }, [isSubmitting, status]);

  const isDisabled = isSubmitting || status === 'success';

  return (
    <form className="mt-4 grid gap-3 md:grid-cols-3" onSubmit={handleSubmit}>
      <input
        className="rounded-xl px-4 py-3 text-gray-900"
        placeholder="Компания*"
        value={company}
        onChange={(e) => { setCompany(e.target.value); setStatus('idle'); setMessage(''); }}
        required
        disabled={isDisabled}
      />
      <input
        className="rounded-xl px-4 py-3 text-gray-900"
        placeholder="Имя, должность*"
        value={author}
        onChange={(e) => { setAuthor(e.target.value); setStatus('idle'); setMessage(''); }}
        required
        disabled={isDisabled}
      />
      <select
        id="rate"
        className="rounded-xl px-4 py-3 text-gray-900"
        value={rating}
        onChange={(e) => { setRating(e.target.value); setStatus('idle'); setMessage(''); }}
        required
        disabled={isDisabled}
      >
        <option value="" disabled>Рейтинг*</option>
        {[5, 4, 3, 2, 1].map(r => <option key={r} value={r}>{r} ★</option>)}
      </select>
      <textarea
        className="rounded-xl px-4 py-3 text-gray-900 md:col-span-3"
        rows={4}
        placeholder="Текст отзыва*"
        value={reviewText}
        onChange={(e) => { setReviewText(e.target.value); setStatus('idle'); setMessage(''); }}
        required
        disabled={isDisabled}
      ></textarea>
      <CustomCheckbox
        name="consent"
        isChecked={isConsentChecked}
        onChange={handleConsentChange}
        link="/privacy"
        textColorClass="text-gray-400"
        linkColorClass="text-[#FF7C22]"
        className="md:col-span-3"
      />
      <button
        type="submit"
        className={`btn ${status === 'success' ? 'bg-green-500 hover:bg-green-600' : 'btn-accent'} md:col-span-3`}
        disabled={isDisabled}
      >
        {buttonText}
      </button>
      {message && (
        <p className={`mt-2 text-sm md:col-span-3 ${status === 'error' ? 'text-red-400' : 'text-green-400'}`}>
          {message}
        </p>
      )}
    </form>
  );
};

const ReviewsPage: React.FC = () => {
  const [q, setQ] = useState('');
  const [sector, setSector] = useState('');
  const [rtype, setRtype] = useState<'text' | 'video' | ''>('');
  const [minR, setMinR] = useState(0);
  const [yearMin, setYearMin] = useState(MIN_YEAR);
  const [yearMax, setYearMax] = useState(MAX_YEAR);
  const [sort, setSort] = useState('date');
  const [page, setPage] = useState(1);
  const [modalReview, setModalReview] = useState<Review | null>(null);

  useEffect(() => {
    if (modalReview) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalReview]);

  const filteredReviews = useMemo(() => {
    let arr = REVIEWS.filter(r => {
      const hay = (r.company + ' ' + r.title + ' ' + (r.excerpt || '') + ' ' + (r.sector || '')).toLowerCase();
      return (
        (!q || hay.includes(q.toLowerCase())) &&
        (!sector || r.sector === sector) &&
        (!rtype || r.type === rtype) &&
        (r.rating >= minR) &&
        (r.year >= yearMin && r.year <= yearMax)
      );
    });

    switch (sort) {
      case 'date':
        arr.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
      case 'rating':
        arr.sort((a, b) => b.rating - a.rating);
        break;
      case 'title':
        arr.sort((a, b) => a.company.localeCompare(b.company, 'ru'));
        break;
      case 'impact':
        arr.sort((a, b) => (Object.keys(b.kpi || {}).length) - (Object.keys(a.kpi || {}).length));
        break;
      default:
        break;
    }
    return arr;
  }, [q, sector, rtype, minR, yearMin, yearMax, sort]);

  const totalReviews = filteredReviews.length;
  const totalPages = Math.max(1, Math.ceil(totalReviews / REVIEWS_PER_PAGE));
  const start = (page - 1) * REVIEWS_PER_PAGE;
  const end = start + REVIEWS_PER_PAGE;
  const paginatedReviews = filteredReviews.slice(start, end);

  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    } else if (page !== 1 && filteredReviews.length > 0 && page > totalPages) {
      setPage(1);
    }
  }, [filteredReviews, totalPages, page]);

  const handleFilterChange = (setter: React.Dispatch<any>) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setter(e.target.value);
    setPage(1);
  };

  const handleYearRangeChange = (setter: React.Dispatch<React.SetStateAction<number>>) => (e: ChangeEvent<HTMLInputElement>) => {
    setter(Number(e.target.value));
    setPage(1);
  };

  const openModalHandler = useCallback((id: string) => {
    const review = REVIEWS.find(r => r.id === id);
    setModalReview(review || null);
  }, []);

  const closeModalHandler = useCallback(() => {
    setModalReview(null);
  }, []);

  const videoCount = REVIEWS.filter(x => x.type === 'video').length;
  const avgRating = (REVIEWS.reduce((s, x) => s + x.rating, 0) / REVIEWS.length).toFixed(1);
  const statsContent = `Всего: <b>${REVIEWS.length}</b> • Видео: <b>${videoCount}</b> Средний рейтинг: <b>${avgRating}</b>`;

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100 min-h-screen">
      <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap gap-3 text-sm">
            <a href="#list" className="chip px-3 py-1 rounded-full hover:bg-white/25">Отзывы</a>
            <a href="#filters" className="chip px-3 py-1 rounded-full hover:bg-white/25">Фильтры</a>
            <a href="#submit-review" className="chip px-3 py-1 rounded-full hover:bg-white/25">Оставить отзыв</a>
          </nav>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Отзывы клиентов</h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Реальные результаты: скорость закрытий, снижение стоимости найма, рост явки и качества. Текстовые и видео-кейсы.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#list" className="btn btn-ghost">Смотреть отзывы</a>
            <a href="#submit-review" className="btn btn-accent">Оставить отзыв</a>
          </div>
        </div>
      </header>
      <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
        <section id="filters" aria-label="Фильтры" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Подбор по параметрам</h2>
          <div className="grid gap-3 md:grid-cols-6">
            <input
              id="q"
              className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
              placeholder="Поиск по компании/кейсу…"
              value={q}
              onChange={handleFilterChange(setQ)}
            />
            <select id="sector" className="rounded-xl px-4 py-3 text-gray-900" value={sector} onChange={handleFilterChange(setSector)}>
              <option value="">Отрасль: все</option>
              {SECTORS.map(s => <option key={s}>{s}</option>)}
            </select>
            <select id="rtype" className="rounded-xl px-4 py-3 text-gray-900" value={rtype} onChange={handleFilterChange(setRtype)}>
              <option value="">Тип: все</option>
              <option value="text">Текст</option><option value="video">Видео</option>
            </select>
            <select id="minr" className="rounded-xl px-4 py-3 text-gray-900" value={minR} onChange={handleFilterChange(setMinR)}>
              <option value="0">Рейтинг: от 0★</option> <option value="1">от 1★</option> <option value="2">от 2★</option>
              <option value="3">от 3★</option><option value="4">от 4★</option><option value="5">только 5★</option>
            </select>
            <div className="rounded-xl px-4 py-3 card md:col-span-2">
              <label className="text-sm text-gray-200">Годы</label>
              <input
                id="y1" type="range" min={MIN_YEAR} max={MAX_YEAR} value={yearMin}
                className="w-full" onChange={handleYearRangeChange(setYearMin)}
              />
              <span id="y1v" className="text-sm">{yearMin}</span>
              <input
                id="y2" type="range" min={MIN_YEAR} max={MAX_YEAR} value={yearMax}
                className="w-full" onChange={handleYearRangeChange(setYearMax)}
              />
              <span id="y2v" className="text-sm">{yearMax}</span>
            </div>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <select id="sort" className="rounded-xl px-4 py-3 text-gray-900" value={sort} onChange={handleFilterChange(setSort)}>
              <option value="date">Сортировать: по дате ↓</option>
              <option value="rating">по рейтингу</option>
              <option value="title">по компании</option>
              <option value="impact">по эффекту (KPI)</option>
            </select>
            <div
              className="card rounded-xl px-4 py-3 text-sm text-gray-200"
              id="stats"
              dangerouslySetInnerHTML={{ __html: statsContent }}
            ></div>
          </div>
        </section>

        <section id="list" aria-label="Список отзывов" className="scroll-mt-32">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <h2 className="text-3xl md:text-4xl font-bold">Отзывы</h2>
            <span id="count" className="text-sm text-gray-300">Найдено отзывов: {totalReviews}</span>
          </div>
          <div id="grid" className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {paginatedReviews.map(r => (
              <ReviewCard key={r.id} review={r} openModal={openModalHandler} />
            ))}
          </div>
          <p id="empty" className={totalReviews === 0 ? 'text-gray-300' : 'hidden text-gray-300'}>
            Ничего не найдено — измените фильтры.
          </p>
          <div id="pager" className="mt-6 flex flex-wrap items-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(i => (
              <button
                key={i}
                className={`px-3 py-2 rounded-lg ${i === page ? 'btn-accent' : 'btn-ghost'}`}
                onClick={() => setPage(i)}
              >
                {i}
              </button>
            ))}
          </div>
        </section>

        <section id="submit-review" className="simple-card scroll-mt-32">
          <h3 className="text-2xl font-bold">Оставить отзыв</h3>
          <p className="text-sm text-gray-200 mt-1">Ваше мнение помогает другим выбрать правильное решение.</p>
          <ReviewSubmissionForm />
        </section>
      </main>
      <ReviewModal review={modalReview} onClose={closeModalHandler} />
    </div>
  );
};

export default ReviewsPage;